import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Button, Modal, Form, Alert } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faInfoCircle, faChartLine, faSave } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/api';
import Ranked from '../components/Ciem/Ranked';
import CardLayout from '../components/Layout/CardLayout';

const TasaRetornoPage = () => {
    const [tasaRetorno, setTasaRetorno] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showAyudaModal, setShowAyudaModal] = useState(false);
    const [showRecuerdeModal, setShowRecuerdeModal] = useState(false);

    const ciemId = useSelector(state => state.ciem.ciemId);
    const [rankedForm, setRankedForm] = useState([]);

    // Load tasa retorno from API
    useEffect(() => {
        if (ciemId) {
            fetchCiemData();
        } else {
            setLoading(false);
        }

        axiosInstance.get('/detailoption/option/value/rankedEleven').then((response) => {
            console.log("Calificación obtenida:", response.data.data[0]);
            setRankedForm(response.data.data[0]);
        }).catch((e) => {
            alert("Error consultando la calificacion " + e)
        });
    }, [ciemId]);

    const fetchCiemData = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/ciem/${ciemId}`);
            // Handle nested data structure if needed
            const data = response.data.data || response.data;
            setTasaRetorno(data.tasaRetorno !== undefined && data.tasaRetorno !== null ? data.tasaRetorno : '');
            setError(null);
        } catch (err) {
            setError('Error al cargar los datos del CIEM: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!ciemId) {
            setError('No se ha seleccionado un CIEM');
            return;
        }

        try {
            setLoading(true);
            await axiosInstance.patch(`/ciem/tasaRetorno/${ciemId}`, parseFloat(tasaRetorno), {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            setSuccess('Tasa de retorno actualizada correctamente');
            setError(null);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Error al actualizar la tasa de retorno: ' + err.message);
            setSuccess(null);
        } finally {
            setLoading(false);
        }
    };

    if (!ciemId) {
        return (
            <Container className="mt-4">
                <Alert variant="warning">
                    Por favor, seleccione un CIEM para editar la tasa de retorno.
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="px-0">
            <Row>
                <Col xs={12} className="p-3">
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center">
                                    <h3 className="mb-0 me-3">
                                        <FontAwesomeIcon icon={faChartLine} className="me-2" />
                                        Tasa de Retorno
                                    </h3>
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => setShowAyudaModal(true)}
                                        title="Ayuda"
                                    >
                                        <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
                                        Ayuda
                                    </Button>
                                    <Button
                                        variant="outline-warning"
                                        size="sm"
                                        onClick={() => setShowRecuerdeModal(true)}
                                        title="Recuerde"
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                        Recuerde
                                    </Button>
                                </div>
                                <Button
                                    variant="primary"
                                    onClick={handleSave}
                                    disabled={loading}
                                >
                                    <FontAwesomeIcon icon={faSave} className="me-2" />
                                    Guardar
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                            {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <Form.Group>
                                        <Form.Label>Tasa de Retorno (%)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            placeholder="Ingrese la tasa de retorno"
                                            value={tasaRetorno}
                                            onChange={(e) => setTasaRetorno(e.target.value)}
                                        />
                                        <Form.Text className="text-muted">
                                            Ingrese el porcentaje de tasa de retorno esperada para el proyecto.
                                        </Form.Text>
                                    </Form.Group>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modal de Ayuda */}
            <Modal show={showAyudaModal} onHide={() => setShowAyudaModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Ayuda</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ textAlign: 'justify' }}>
                        La tasa de retorno es el porcentaje de rendimiento esperado sobre la inversión realizada en el proyecto.
                        Este valor es fundamental para evaluar la viabilidad financiera y comparar diferentes alternativas de inversión.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAyudaModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Recuerde */}
            <Modal show={showRecuerdeModal} onHide={() => setShowRecuerdeModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Recuerde</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ textAlign: 'justify' }}>
                        Una tasa de retorno adecuada debe considerar el costo de oportunidad del capital, el riesgo asociado al proyecto
                        y las expectativas del mercado. Es recomendable comparar esta tasa con otras inversiones similares en el sector
                        para asegurar que el proyecto sea competitivo y atractivo para los inversionistas.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRecuerdeModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Ranked - Calificación */}
            {(() => {
                const calificacion = <Ranked field={rankedForm} ciemId={ciemId} />;
                return <CardLayout value={"rankedEleven"} tablaComponent={calificacion} />;
            })()}
        </Container>
    );
};

export default TasaRetornoPage;
